import {
  type Maybe,
  type ProjectListTableDataQuery,
  ProjectSubrowsDocument,
  type ProjectSubrowsQuery,
  type ProjectSubrowsQueryVariables,
} from "@src/__generated__/graphql";
import { FetchHelper } from "@src/helpers/apollo/fetch";
import { action, computed, makeObservable, observable } from "mobx";
import type { MarkRequired } from "ts-essentials";

type TProject = NonNullable<ProjectListTableDataQuery["projects"]>["data"][0];

export class Project {
  id: TProject["id"];
  title: TProject["title"];
  code: string;
  numeric_code: TProject["numeric_code"];
  brand: TProject["brand"];
  note?: TProject["note"];
  projectManager: TProject["projectManager"];
  otherProjectManagers?: TProject["otherProjectManagers"];
  budgetovacBudget?: TProject["budgetovacBudget"];
  budgetovacBudgets?: TProject["budgetovacBudgets"];
  ourWorkOverview?: TProject["ourWorkOverview"];
  expensesStructured?: TProject["expensesStructured"];
  stats?: TProject["statsCached"];
  @observable.ref
  lastWorkInProgressProjectStat?: TProject["lastWorkInProgressProjectStat"];
  billingPlanIsMissing?: TProject["billingPlanIsMissing"];
  @observable.ref billable?: TProject["billable"];
  type?: TProject["type"];
  is_pinned: TProject["is_pinned"];
  finished_at?: Maybe<string>;
  archived_at?: Maybe<string>;
  cloudUrl?: TProject["cloudUrl"];
  categories?: TProject["categories"];
  scopes?: TProject["scopes"];
  @observable.ref isTemplate: TProject["is_template"];
  stage: TProject["project_stage"];
  @observable.ref
  is_starred: TProject["is_starred"];

  @observable.ref
  __subRows: SubRow[];

  private subrowsFetcher = new FetchHelper<
    ProjectSubrowsQuery,
    ProjectSubrowsQueryVariables
  >(ProjectSubrowsDocument);

  constructor(
    src: MarkRequired<Partial<TProject>, "is_pinned" | "projectManager">,
  ) {
    makeObservable(this);
    this.id = src.id!;
    this.title = src.title!;
    this.code = src.code!;
    this.numeric_code = src.numeric_code;
    this.note = src.note;
    this.brand = src.brand!;
    this.projectManager = src.projectManager;
    this.otherProjectManagers = src.otherProjectManagers;
    this.budgetovacBudget = src.budgetovacBudget;
    this.budgetovacBudgets = src.budgetovacBudgets;
    this.ourWorkOverview = src.ourWorkOverview;
    this.expensesStructured = src.expensesStructured;
    this.stats = src.statsCached;
    this.lastWorkInProgressProjectStat = src.lastWorkInProgressProjectStat;
    this.billingPlanIsMissing = src.billingPlanIsMissing;
    this.billable = src.billable;
    this.is_pinned = src.is_pinned;
    this.finished_at = src.finished_at;
    this.archived_at = src.archived_at;
    this.cloudUrl = src.cloudUrl;
    this.type = src.type;
    this.scopes = src.scopes;
    this.categories = src.categories ?? [];
    this.isTemplate = src.is_template ?? false;
    this.stage = src.project_stage;
    this.is_starred = src.is_starred ?? false;
    this.__subRows =
      src.ourWork?.map(
        (item) =>
          new SubRow({
            id: item.id,
            title: "",
            stats: {
              id: "",
              invoiced_percent: 0,
              spent_time: 0,
              spent_budget: 0,
              total_budget: 0,
              credit_balance: 0,
              budget_exceeded: 0,
              spent_budget_percent: 0,
            },
            category: {
              name: "",
            },
          }),
      ) ?? [];
  }

  @computed
  get isLoadingSubrows(): boolean {
    return this.subrowsFetcher.isLoading.value;
  }

  @computed
  get canFetchSubrows(): boolean {
    if (this.__subRows.length === 0) return false;
    return this.__subRows.every((subrow) => subrow.title === "");
  }

  @action.bound
  updateCurrentWorkInProgressItemStat(
    src: NonNullable<TProject["lastWorkInProgressProjectStat"]>,
  ) {
    this.lastWorkInProgressProjectStat = src;
  }

  @action.bound
  async fetchSubrows(): Promise<void> {
    if (!this.__subRows.length) return;
    // NOTE: Subrows are loaded so there is no need to fetch them again
    if (!this.canFetchSubrows) return;

    const [data, error] = await this.subrowsFetcher.fetch(
      {
        id: this.id,
      },
      null,
    );

    if (error) return;

    this.__subRows = data.project?.ourWork.map((i) => new SubRow(i)) ?? [];
  }

  @computed
  get hasBudgetsInternallyApproved() {
    return (
      this.budgetovacBudgets
        ?.filter((budget) => budget.is_active)
        .every((budget) => budget.internally_approved) ?? true
    );
  }
}

type TOurWork = NonNullable<ProjectSubrowsQuery["project"]>["ourWork"][0];

export class SubRow {
  id: TOurWork["id"];
  @observable.ref
  title: TOurWork["title"];
  stats: TOurWork["stats"];
  categoryName: TOurWork["category"]["name"];
  @observable
  lastWorkInProgressItemStat: TOurWork["lastWorkInProgressItemStat"];

  constructor(src: TOurWork) {
    makeObservable(this);
    this.id = src.id;
    this.title = src.title;
    this.stats = src.stats;
    this.lastWorkInProgressItemStat = src.lastWorkInProgressItemStat;
    this.categoryName = src.category.name;
  }

  @action.bound
  updateCurrentWorkInProgressItemStat(
    src: Omit<
      NonNullable<SubRow["lastWorkInProgressItemStat"]>,
      "work_in_progress_id"
    >,
  ) {
    if (this.lastWorkInProgressItemStat) {
      this.lastWorkInProgressItemStat = {
        ...this.lastWorkInProgressItemStat,
        ...src,
      };
    }
  }
}
